import { Controller } from "@hotwired/stimulus";

type SignageData = {
  name: string;
  interval: number;
  url: string;
  anime_target?: string;
};

export default class extends Controller {
  private playSignage: SignageData[] = [];
  private index: number = 0;
  private animationFrameId: number | null = null;
  private isPaused: boolean = false;
  private spendMs: number = 0; // 一時停止をおしたときに経過した時間を記録
  private elapsedMs: number = 0; // 経過時間を記録

  connect(): void {
    const raw = this.element.getAttribute("data-play-signage");
    this.playSignage = raw ? JSON.parse(raw) : [];

    this.loadSlideshow();

    setTimeout(() => {
      this.displaySlide(0, true);
    }, 100);
  }

  disconnect(): void {
    this.cancelCountdownLoop();
  }

  pause(): void {
    this.isPaused = true;
    this.spendMs = this.elapsedMs;
    this.cancelCountdownLoop();
  }

  play(): void {
    this.isPaused = false;
    this.displaySlide(this.index);
  }

  /* properties */
  private get screens(): HTMLElement[] {
    return Array.from(this.element.querySelectorAll<HTMLElement>("[data-signage-target='screen']"));
  }

  private loadSlideshow(): void {
    this.screens.forEach((screen, index) => {
      const frame = screen.querySelector<HTMLIFrameElement>("iframe");
      if (frame) {
        frame.src = this.playSignage[index].url;
      }
    });
  }

  private cancelCountdownLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private displaySlide(index: number, firstActive = false): void {
    this.index = index;
    const slide = this.playSignage[this.index];

    this.dispatch("slideChanged", {
      detail: { slide_index: this.index, slide },
      bubbles: true,
    });

    this.scrollIframe(index, firstActive);
    this.highlightCurrentScreen(index, firstActive);
  }

  private nextSlide(): void {
    const nextIndex = (this.index + 1) % this.playSignage.length;

    // スライドの切替がない場合は初期ロードとして扱う
    if (this.index === nextIndex) this.displaySlide(nextIndex, true);
    else this.displaySlide(nextIndex);
  }

  private highlightCurrentScreen(index: number, firstActive = false): void {
    let localTimeoutId: number | null = null;

    this.screens.forEach((screen, i) => {
      if (i !== index) {
        screen.classList.remove("active");
        return;
      }

      if (!firstActive) {
        screen.classList.add("active");
        return;
      }

      // 最初のiframeのみ遅延してactive付与
      if (localTimeoutId) {
        clearTimeout(localTimeoutId);
      }

      localTimeoutId = window.setTimeout(() => {
        screen.classList.add("active");
      }, 400);
    });
  }

  private scrollIframe(index: number, firstActive: boolean = false): void {
    const screen = this.screens[index];
    const frame = screen?.querySelector<HTMLIFrameElement>("iframe");
    const slide = this.playSignage[index];

    if (!frame || !slide) return;

    if (firstActive) {
      // 初回表示時は onload 経由でのみスクロールを開始する
      frame.addEventListener(
        "load",
        () => {
          this.runScrollAnimation(frame, slide.interval * 1000);
        },
        { once: true },
      );
    } else {
      this.runScrollAnimation(frame, slide.interval * 1000);
    }
  }

  private runScrollAnimation(iframe: HTMLIFrameElement, durationMs: number): void {
    try {
      const win = iframe.contentWindow;
      if (!win) return;

      const slide = this.playSignage[this.index];
      let startTime: number | null = null;
      const animate = (time: number) => {
        if (this.isPaused) return;

        const doc = win.document;
        const { slideElement, overflowHeight } = (() => {
          let slideElement: HTMLElement | Window;
          let overflowHeight: number;
          if (slide.anime_target) {
            slideElement = doc.querySelector<HTMLElement>(slide.anime_target) || doc.body;
            overflowHeight = slideElement.scrollHeight - slideElement.clientHeight;
          } else {
            slideElement = win;
            overflowHeight = doc.body.scrollHeight - win.innerHeight;
          }
          return { slideElement, overflowHeight };
        })();

        if (!startTime) startTime = time;
        this.elapsedMs = time - startTime + this.spendMs;
        const progress = Math.min(this.elapsedMs / durationMs, 1);
        const scrollY = overflowHeight * progress;

        slideElement.scrollTo(0, scrollY);

        if (progress < 1) {
          this.animationFrameId = win.requestAnimationFrame(animate);
        } else {
          slideElement.scrollTo(0, 0);
          this.spendMs = 0;

          // 次のスライドへ以降するタイミングで最新情報を取得しておく
          iframe.src = slide.url;
          this.nextSlide();
        }
      };

      this.animationFrameId = win.requestAnimationFrame(animate);
    } catch (e) {
      console.error("iframe scroll failed:", e);
    }
  }
}
